import { HttpClient, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { map, Observable } from "rxjs";
import { User } from "../../../models/api/user";

// const USER_NOTIFICATION_SETTINGS = {
//   internalServiceContractsReminder: {
//     app: false,
//     email: false,
//   },
//   internalServiceContracts: {
//     app: false,
//     email: false,
//   },
//   externalServiceContractsReminder: {
//     app: false,
//     email: false,
//   },
//   externalServiceContracts: {
//     app: true,
//     email: false,
//   },
//   predictiveServiceInspection: {
//     app: false,
//     email: false,
//   },
//   predictiveServiceReplacement: {
//     app: false,
//     email: false,
//   },
//   repairService: {
//     app: false,
//     email: false,
//   },
//   warning: {
//     app: true,
//     email: false,
//   },
//   critical: {
//     app: false,
//     email: false,
//   },
//   energyLoss: {
//     app: false,
//     email: false,
//   },
//   suspiciousActivity: {
//     app: false,
//     email: false,
//   },
// };

@Injectable({
  providedIn: "root",
})
export class UserService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Retrieves all users for a given customer.
   * @param customerId The ID of the customer to retrieve users for.
   * @returns An observable of the users array.
   */
  public getUsersByCustomer(customerId: string) {
    return this.http.get<User[]>(environment.baseUrl + `/user`, { observe: "response" }).pipe(
      this.apiErrorHandler.handle("user"),
      map((res) => {
        if (res.body) {
          const users = res.body;
          return users.filter((user) => user.customer?.id === customerId);
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Retrieves all users.
   * @returns An observable of the users array.
   */
  public getUsers(): Observable<User[]> {
    return this.http.get<User[]>(environment.baseUrl + `/user`, { observe: "response" }).pipe(
      this.apiErrorHandler.handle("user"),
      map((res: HttpResponse<User[]>) => {
        if (res.body) {
          const users = res.body;
          return users;
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Retrieves a user by their ID.
   * @param userId The ID of the user to retrieve.
   * @returns An observable of the user object.
   */
  public getUserDetails(userId: string): Observable<User> {
    return this.http.get<User>(environment.baseUrl + `/user/${userId}`).pipe(this.apiErrorHandler.handle("user"));
  }

  /**
   * Creates a new user from a customer.
   * @param user The user object with the relevant user details.
   * @param customerId The ID of the customer to add the user to.
   * @returns An observable of the newly created user object.
   */
  public addUserFromCustomer(user: User, customerId: string) {
    return this.http
      .post(environment.baseUrl + `/user?customerId=${customerId}`, user)
      .pipe(this.apiErrorHandler.handle("user"));
  }

  /**
   * Creates a new user from a tenant.
   * @param user The user object with the relevant user details.
   * @param tenantId The ID of the tenant to add the user to.
   * @returns An observable of the newly created user object.
   */
  public addUserFromTenant(user: User, tenantId: string | undefined) {
    user.twoFactorEnabled = false;
    return this.http
      .post(environment.baseUrl + `/user?tenantId=${tenantId}`, user)
      .pipe(this.apiErrorHandler.handle("user"));
  }

  /**
   * Updates an existing user's details.
   * @param user The user object containing updated details. The user ID is extracted and used to identify the user.
   * @returns An observable of the HTTP PUT request to update the user.
   */
  public editUser(user: User) {
    const userId = user.id;
    delete user.id;
    return this.http.put(environment.baseUrl + `/user/${userId}`, user).pipe(this.apiErrorHandler.handle("user"));
  }

  /**
   * Deletes a user with the given ID.
   * @param userId The ID of the user to delete.
   * @returns An observable of the HTTP DELETE request to delete the user.
   */
  public deleteUser(userId: string) {
    return this.http.delete(environment.baseUrl + `/user/${userId}`).pipe(this.apiErrorHandler.handle("user"));
  }
}
